/* eslint-disable react/no-array-index-key, react/no-string-refs, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import { GithubPicker } from 'react-color';
import {
  ClickAwayListener,
  FormControl,
  FormHelperText,
  InputLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@material-ui/core';
import ColorLens from '@material-ui/icons/ColorLens';

import * as actions from '../../../../state';
import styles from '../styles.module.css';

const messages = defineMessages({
  ADD_ALERT_TOOLTIP: {
    id: 'ADD_ALERT_TOOLTIP',
    defaultMessage: 'Add an alert to help bring attention to potential problems or irregularities.',
  },
  DATA_SET: {
    id: 'DATA_SET',
    defaultMessage: 'Data set',
  },
  DATA_SET_HELPTEXT: {
    id: 'DATA_SET_HELPTEXT',
    defaultMessage: 'Data set to monitor for the alert',
  },
  THRESHOLD_HELPTEXT: {
    id: 'THRESHOLD_HELPTEXT',
    defaultMessage: 'Threshold to trigger the alert',
  },
  ERROR_THRESHOLD_REQUIRED: {
    id: 'ERROR_THRESHOLD_REQUIRED',
    defaultMessage: 'Threshold is required',
  },
  ABOVE: {
    id: 'ABOVE',
    defaultMessage: 'Above',
  },
  BELOW: {
    id: 'BELOW',
    defaultMessage: 'Below',
  },
  WHEN_VALUE_IS: {
    id: 'WHEN_VALUE_IS',
    defaultMessage: 'When value is...',
  },
  THRESHOLD: {
    id: 'THRESHOLD',
    defaultMessage: '...this threshold',
  },
  COLOR: {
    id: 'COLOR',
    defaultMessage: 'Color',
  },
  COLOR_HELPTEXT: {
    id: 'COLOR_HELPTEXT',
    defaultMessage: 'Data will turn this color when passing the threshold',
  },
  NOTIFICATIONS: {
    id: 'NOTIFICATIONS',
    defaultMessage: 'Notifications',
  },
  BROWSER_NOTIFICATION: {
    id: 'BROWSER_NOTIFICATION',
    defaultMessage: 'Browser Notification',
  },
  MINUTE_UNIT_DECORATOR: {
    id: 'MINUTE_UNIT_DECORATOR',
    defaultMessage: 'm',
  },
  SECOND_UNIT_DECORATOR: {
    id: 'SECOND_UNIT_DECORATOR',
    defaultMessage: 's',
  },
  MINUTES: {
    id: 'MINUTES',
    defaultMessage: 'Minutes',
  },
  SECONDS: {
    id: 'SECONDS',
    defaultMessage: 'Seconds',
  },
  AGENTS: {
    id: 'AGENTS',
    defaultMessage: 'Agents',
  },
  CALLS: {
    id: 'CALLS',
    defaultMessage: 'Calls',
  },
  PERCENT: {
    id: 'PERCENT',
    defaultMessage: 'Percent',
  },
});

const colors = [
  // eslint-disable-next-line max-len
  '#FFA727', '#FFCA28', '#FFEE58', '#D4E157', '#9CCC65', '#66BB6A', '#26A69A', '#26C6DA', '#29B6F6', '#42A5F5', '#5C6BC0', '#7D57C2', '#AB46BC', '#EC407A', '#EF5350', '#FFFFFF',
  // eslint-disable-next-line max-len
  '#FF9800', '#FFC106', '#FFEB3B', '#CDDC39', '#8BC34A', '#4CAF51', '#009688', '#01BCD4', '#03A9F4', '#2196F3', '#3F51B5', '#673AB7', '#9C27B0', '#E91E63', '#F44337', '#E0E0E0',
  // eslint-disable-next-line max-len
  '#FB8C00', '#FFB300', '#FDD835', '#C0CA33', '#7CB342', '#43A047', '#00897B', '#01ACC1', '#039BE5', '#1E88E5', '#3949AB', '#5E35B1', '#8E24AA', '#D81B60', '#E53935', '#9E9E9E',
  // eslint-disable-next-line max-len
  '#F57C00', '#FFA000', '#FBC02D', '#AFB42A', '#689F38', '#388E3C', '#00796B', '#0197A7', '#0288D1', '#1976D2', '#30409F', '#512DA8', '#7B1FA2', '#C2195B', '#D84315', '#616161',
  // eslint-disable-next-line max-len
  '#EF6C00', '#FF8F00', '#F9A825', '#9E9D24', '#558B2F', '#2E7D32', '#00695C', '#01838F', '#0277BD', '#1565C0', '#283593', '#4526A0', '#6A1B9A', '#AD1457', '#D32F2F', '#212121',
];

function DataSourceAlertControls({ alertConfig, selectAlert }) {
  const dispatch = useDispatch();

  const chartsManifest = useSelector(actions.readChartsManifest);
  const selectedChart = useSelector(actions.readStateSelectedChart);

  const [selectingColor, setSelectingColor] = useState(false);

  const filterToValidStats = () => {
    const dataSource = selectedChart.data_sources[alertConfig.sourceIndex];
    const tableSource = selectedChart.data_sources[0];
    const chartDef = chartsManifest[selectedChart.type];
    const tableDef = chartDef.dataOptions[tableSource.stat];

    return dataSource.queryParams.stats.filter((stat) => {
      const columnDef = tableDef.supportedColumns.find((column) => column.stat === stat);
      return columnDef.unit !== 'string';
    });
  };

  useEffect(() => {
    // [CHRT-343]
    const chartDef = chartsManifest[selectedChart.type];
    if (chartDef.usesColumns && alertConfig && !alertConfig.column) {
      // eslint-disable-next-line no-param-reassign
      alertConfig.sourceIndex = 0;

      const tableSource = selectedChart.data_sources[0];
      const validStats = filterToValidStats(tableSource.queryParams.stats);
      // eslint-disable-next-line no-param-reassign
      alertConfig.column = validStats.length ? validStats[0] : 0;

      selectAlert(alertConfig);
    }
  }, []);

  const handleSourceSelect = (event) => {
    const config = {
      sourceIndex: 0,
      direction: 'above',
      color: '#ff0000',
      error: false,
    };

    const chartDef = chartsManifest[selectedChart.type];
    const selectedValue = event.target.value;

    if (chartDef.usesColumns) {
      config.sourceIndex = 0;
      config.column = selectedValue;

      if (selectedChart.data_sources[config.sourceIndex].alerts) { // if an alert already exists, lock direction
        const { alerts } = selectedChart.data_sources[config.sourceIndex];
        const alertMatch = alerts.filter((alert) => alert.column === selectedValue);

        if (alertMatch.length > 0) config.direction = alertMatch[0].direction;
      }
    } else {
      config.sourceIndex = selectedValue;

      if (selectedChart.data_sources[config.sourceIndex].alerts) { // if an alert already exists, lock direction
        config.direction = selectedChart.data_sources[config.sourceIndex].alerts[0].direction;
      }
    }

    selectAlert(config);
  };

  const handleDirectionSelect = (event) => {
    selectAlert({ ...alertConfig, direction: event.target.value });
  };

  const handleThresholdChange = (event) => {
    selectAlert({
      ...alertConfig,
      error: false,
      threshold: event.target.value,
    });
  };

  const handleMinChange = (event) => {
    selectAlert({
      ...alertConfig,
      error: false,
      min: event.target.value,
      sec: alertConfig.sec || 0,
      threshold: Number.parseInt(event.target.value * 60, 10)
        + Number.parseInt((alertConfig.sec || 0), 10),
    });
  };

  const handleSecChange = (event) => {
    selectAlert({
      ...alertConfig,
      error: false,
      min: alertConfig.min || 0,
      sec: event.target.value > 59 ? 59 : event.target.value,
      threshold: Number.parseInt(alertConfig.min * 60, 10)
        + Number.parseInt((event.target.value > 59 ? 59 : event.target.value), 10),
    });
  };

  const toggleColorSelect = () => {
    setSelectingColor(!selectingColor);
  };

  const handleColorSelect = (color) => {
    selectAlert({
      ...alertConfig,
      color: color.hex,
    });

    if (selectedChart.data_sources[alertConfig.sourceIndex].alerts) { // if this alert object exists, update it
      selectedChart.data_sources[alertConfig.sourceIndex].alerts[alertConfig.index] = alertConfig;
      dispatch(actions.updateSelectedChart(selectedChart));
    }

    toggleColorSelect();
  };

  const handleBroNoToggle = (event, value) => {
    selectAlert({ ...alertConfig, bronoEnabled: value });
  };

  const alertSourceOptions = () => {
    const chartDef = chartsManifest[selectedChart.type];

    if (chartDef.usesColumns) {
      const tableSource = selectedChart.data_sources[0];
      const tableDef = chartDef.dataOptions[tableSource.stat];
      const validStats = filterToValidStats(tableSource.queryParams.stats);

      return validStats.map((stat, sId) => (
        <MenuItem
          key={`stat_${sId}`}
          value={stat}
        >
          {tableDef.supportedColumns.find((column) => column.stat === stat).label}
        </MenuItem>
      ));
    }
    return selectedChart.data_sources.map((source, sId) => (
      <MenuItem
        key={`source_${sId}`}
        value={sId}
      >
        {chartDef.dataOptions[source.stat].label}
      </MenuItem>
    ));
  };

  const isCustomData = selectedChart.data_sources[0].stat === 'custom';
  const dataSource = selectedChart.data_sources[alertConfig.sourceIndex];
  const sourceAlerts = dataSource.alerts;
  const hasMultipleAlerts = sourceAlerts && Boolean(sourceAlerts.length);
  const statDef = chartsManifest[selectedChart.type].dataOptions[dataSource.stat] || {};

  let dataUnit = statDef ? statDef.unit : null;

  /* derive a default selected value for the Data set options, using an ugly if/else monstrosity */
  let defaultSelectValue = 0; // default default is just index 0
  if (!isCustomData) { // make sure this isn't a custom source before anything
    if (statDef.hasColumns) { // if this stat has columns
      if (alertConfig.column) { // if the alert config in state has a column selected
        defaultSelectValue = alertConfig.column; // ...apply the selected column
      } else { // otherwise
        const validStats = filterToValidStats(dataSource.queryParams.stats);
        // eslint-disable-next-line prefer-destructuring
        defaultSelectValue = validStats[0]; // ...apply the first valid column
      }
    } else { // if this is a non-column source
      defaultSelectValue = alertConfig.sourceIndex; // use the selected sourceIndex, which may default to 0
    }
  }

  /* if a column is selected, update the dataUnit */
  if (statDef.hasColumns) {
    const chartDef = chartsManifest[selectedChart.type];
    const tableDef = chartDef.dataOptions[dataSource.stat];
    const columnDef = tableDef.supportedColumns.find((column) => column.stat === defaultSelectValue);
    dataUnit = columnDef.unit;
  }

  /* if a previous alert was added, lock the alert direction */
  let alertDirection = (alertConfig && alertConfig.direction) || 'above';
  let alertDirectionLocked = false;

  if (statDef.hasColumns && hasMultipleAlerts) {
    const alertMatch = sourceAlerts.filter((alert) => alert.column === defaultSelectValue);
    if (alertMatch.length > 1) {
      alertDirectionLocked = true;
      alertDirection = alertMatch[0].direction;
    }
  } else if (sourceAlerts && sourceAlerts.length > 1) {
    alertDirectionLocked = true;
  } else if (sourceAlerts && sourceAlerts.length === 1 && alertConfig.index === undefined) {
    alertDirectionLocked = true;
  }

  return (
    <div className={styles.list}>
      <List dense className={styles.menu}>
        <ListItem className={styles.helptext}>
          <FormattedMessage {...messages.ADD_ALERT_TOOLTIP} />
        </ListItem>

        {!isCustomData && (
          <ListItem>
            <FormControl style={{ width: '100%' }}>
              <InputLabel shrink>
                <FormattedMessage {...messages.DATA_SET} />
              </InputLabel>

              <Select
                name="DATA_SET"
                value={defaultSelectValue}
                onChange={handleSourceSelect}
                displayEmpty
              >
                {alertSourceOptions()}
              </Select>

              <FormHelperText>
                <FormattedMessage {...messages.DATA_SET_HELPTEXT} />
              </FormHelperText>
            </FormControl>
          </ListItem>
        )}

        <ListItem>
          <FormControl style={{ width: '48%' }}>
            <InputLabel shrink>
              <FormattedMessage {...messages.WHEN_VALUE_IS} />
            </InputLabel>

            <Select
              name="alert_direction"
              value={alertDirection}
              onChange={handleDirectionSelect}
              displayEmpty
            >
              <MenuItem
                key="above"
                value="above"
                disabled={alertDirectionLocked && alertDirection && alertDirection === 'below'}
              >
                <FormattedMessage {...messages.ABOVE} />
              </MenuItem>

              <MenuItem
                key="below"
                value="below"
                disabled={alertDirectionLocked && alertDirection && alertDirection === 'above'}
              >
                <FormattedMessage {...messages.BELOW} />
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl style={{ width: '48%', marginLeft: 10 }}>
            <InputLabel shrink>
              <FormattedMessage {...messages.THRESHOLD} />
            </InputLabel>

            {dataUnit === 'minutes' && (
              <div>
                <TextField
                  autoFocus
                  type="number"
                  className={styles.thresholdHalfInput}
                  value={alertConfig.min}
                  error={alertConfig.error === 'threshold'}
                  onChange={handleMinChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <FormattedMessage {...messages.MINUTE_UNIT_DECORATOR} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  autoFocus
                  type="number"
                  className={styles.thresholdHalfInput}
                  value={alertConfig.sec}
                  error={alertConfig.error === 'threshold'}
                  onChange={handleSecChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <FormattedMessage {...messages.SECOND_UNIT_DECORATOR} />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            )}

            {dataUnit !== 'minutes' && (
              <TextField
                autoFocus
                type="number"
                className={styles.thresholdInput}
                value={alertConfig.threshold || ''}
                error={alertConfig.error === 'threshold'}
                onChange={handleThresholdChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span>
                        {dataUnit && <FormattedMessage {...messages[dataUnit.toUpperCase()]} />}
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {alertConfig.error === 'threshold' && (
              <FormHelperText className={styles.errorHelpText}>
                <FormattedMessage {...messages.ERROR_THRESHOLD_REQUIRED} />
              </FormHelperText>
            )}
          </FormControl>
        </ListItem>

        <ListItem>
          <FormHelperText style={{ width: '100%', marginTop: 12 }}>
            <FormattedMessage {...messages.THRESHOLD_HELPTEXT} />
          </FormHelperText>
        </ListItem>

        <ListItem>
          <div className={styles.sectionHeader} style={{ display: 'block' }}>
            <FormattedMessage {...messages.COLOR} />
          </div>
        </ListItem>

        <ListItem>
          <div
            className={styles.selectedColor}
            style={{ backgroundColor: alertConfig.color }}
            onClick={toggleColorSelect}
          >
            <ColorLens className={styles.colorLensIcon} />
          </div>

          {selectingColor && (
            <ClickAwayListener onClickAway={toggleColorSelect}>
              <GithubPicker
                width="100%"
                triangle="hide"
                colors={colors}
                onChangeComplete={handleColorSelect}
              />
            </ClickAwayListener>
          )}
        </ListItem>

        <ListItem>
          <FormHelperText style={{ width: '100%' }}>
            <FormattedMessage {...messages.COLOR_HELPTEXT} />
          </FormHelperText>
        </ListItem>

        <ListItem>
          <div className={styles.sectionHeader} style={{ display: 'block' }}>
            <FormattedMessage {...messages.NOTIFICATIONS} />
          </div>
        </ListItem>

        <ListItem>
          <ListItemText
            primary={<FormattedMessage {...messages.BROWSER_NOTIFICATION} />}
            className={styles.switchTitle}
          />

          <ListItemSecondaryAction className={styles.switchBtn}>
            <Switch
              className={styles.switchComponent}
              checked={alertConfig.bronoEnabled || false}
              onChange={handleBroNoToggle}
            />
          </ListItemSecondaryAction>
        </ListItem>
      </List>
    </div>
  );
}

export default DataSourceAlertControls;
